import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SYSTEM_TYPES = [
  'TUATH_DRAÍOCHTA.BADB_DOMAIN',
  'TUATH_DRAÍOCHTA.MACHA_DOMAIN',
  'TUATH_DRAÍOCHTA.MORRÍGAN_DOMAIN',
  'TUATH_DRAÍOCHTA.LAND_SOVEREIGN',
  'TUATH_DRAÍOCHTA.BATTLE_SOVEREIGN',
  'TUATH_DRAÍOCHTA.SPIRIT_SOVEREIGN',
  'CYBERPUNK_NEURAL_NET',
  'CYBERPUNK_NANITE_SWARM',
  'CYBERPUNK_DIGITAL_CONSCIOUSNESS',
  'CYBERPUNK_QUANTUM_MATRIX',
  'CYBERPUNK_AI_COLLECTIVE'
];

const CONFLICT_PATTERNS = [
  'RÍASTRAD_DIGITIZATION_ERROR',
  'SOVEREIGNTY_PROTOCOL_VIOLATION',
  'OTHERWORLD_NETWORK_DESYNC',
  'BLOOD_OATH_ENCRYPTION_FAILURE',
  'SHAPESHIFTING_BUFFER_OVERFLOW',
  'PROPHECY_QUANTUM_PARADOX',
  'SACRED_WATER_CORRUPTION',
  'TRIPLE_SOVEREIGNTY_FIREWALL_BREACH'
];

const ERROR_DETAILS = {
  'RÍASTRAD_DIGITIZATION_ERROR': 'Cannot safely encode warp-spasm transformations into digital matrix. Risk of catastrophic system corruption.',
  'SOVEREIGNTY_PROTOCOL_VIOLATION': 'Triple Sovereignty (Land, Battle, Spirit) incompatible with current security protocols.',
  'OTHERWORLD_NETWORK_DESYNC': 'Síd realm connection cannot maintain stability within neural network architecture.',
  'BLOOD_OATH_ENCRYPTION_FAILURE': 'Blood and oath-based authentication failing to translate to digital signatures.',
  'SHAPESHIFTING_BUFFER_OVERFLOW': 'Crow\'s Flight transformation data exceeding allocated memory bounds.',
  'PROPHECY_QUANTUM_PARADOX': 'Bean Nighe visions creating temporal paradoxes in quantum prediction matrices.',
  'SACRED_WATER_CORRUPTION': 'Digital corruption detected when processing ritual washing data streams.',
  'TRIPLE_SOVEREIGNTY_FIREWALL_BREACH': 'Land-Battle-Spirit triangulation breaching system security layers.'
};

const SYSTEM_DESCRIPTIONS = {
  'TUATH_DRAÍOCHTA.BADB_DOMAIN': {
    name: "The Crow's Path (Badb's Domain)",
    effects: ["Washer at the Ford - Prophetic death visions", "Crow's Flight - Corvid transformation"]
  },
  'TUATH_DRAÍOCHTA.MACHA_DOMAIN': {
    name: "The Warrior's Path (Macha's Domain)",
    effects: ["Ríastrad - Seven-stage combat transformation", "Battle Frenzy - Superhuman combat abilities"]
  },
  'TUATH_DRAÍOCHTA.MORRÍGAN_DOMAIN': {
    name: "The Phantom Queen's Domain",
    effects: ["Sovereignty Binding", "Battle Prophecy"]
  },
  'TUATH_DRAÍOCHTA.LAND_SOVEREIGN': {
    name: "Talam Sovereignty",
    effects: ["Land-bond manifestation", "Sacred site empowerment"]
  },
  'TUATH_DRAÍOCHTA.BATTLE_SOVEREIGN': {
    name: "Cath Sovereignty",
    effects: ["Warrior's oath enhancement", "Blood ritual amplification"]
  },
  'TUATH_DRAÍOCHTA.SPIRIT_SOVEREIGN': {
    name: "Síd Sovereignty",
    effects: ["Otherworld channeling", "Spirit realm navigation"]
  }
};

const MagiTechConflictModal = ({ isOpen, onClose }) => {
  const [selectedSystems, setSelectedSystems] = useState([]);
  const [conflictAnalysis, setConflictAnalysis] = useState(null);
  const [resolutionPhase, setResolutionPhase] = useState(0);
  const [harmonicConvergence, setHarmonicConvergence] = useState(0);
  const [criticalErrors, setCriticalErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const analyzeConflict = () => {
    if (selectedSystems.length !== 2) return;
    
    // Generate 4 critical errors and 2 warnings
    const errors = [];
    const newWarnings = [];
    
    // Ensure we get different patterns
    const shuffledPatterns = [...CONFLICT_PATTERNS].sort(() => Math.random() - 0.5);
    
    // Take first 4 for critical errors
    errors.push(...shuffledPatterns.slice(0, 4).map(pattern => ({
      pattern,
      details: ERROR_DETAILS[pattern],
      severity: Math.floor(Math.random() * 30) + 70 // 70-100 severity for critical
    })));
    
    // Take next 2 for warnings
    newWarnings.push(...shuffledPatterns.slice(4, 6).map(pattern => ({
      pattern,
      details: ERROR_DETAILS[pattern],
      severity: Math.floor(Math.random() * 30) + 40 // 40-70 severity for warnings
    })));

    setCriticalErrors(errors);
    setWarnings(newWarnings);
    
    setConflictAnalysis({
      pattern: errors[0].pattern,
      severity: Math.max(...errors.map(e => e.severity)),
      resolutionProbability: Math.floor(Math.random() * 100),
      harmonicResonance: Math.floor(Math.random() * 100)
    });
  };

  const initiateResolution = () => {
    setResolutionPhase(prev => prev + 1);
    setHarmonicConvergence(prev => Math.min(prev + Math.random() * 30, 100));
    
    // Randomly resolve some errors and warnings as resolution progresses
    if (resolutionPhase > 0) {
      setCriticalErrors(prev => prev.slice(1));
      if (resolutionPhase > 2) {
        setWarnings(prev => prev.slice(1));
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative bg-purple-950/90 border-2 border-cyan-400 p-8 rounded-lg w-full max-w-4xl mx-4 overflow-y-auto max-h-[90vh]"
          >
            <h2 className="text-cyan-400 text-3xl mb-6 font-['VT323'] glitch-text">
              WORLD_MERGE_CONFLICT_RESOLVER.exe
            </h2>

            <div className="space-y-6">
              {/* System Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-fuchsia-400 mb-2">TÚATH_DRAÍOCHTA_SYSTEMS</h3>
                  <div className="space-y-2">
                    {SYSTEM_TYPES.filter(sys => sys.startsWith('TUATH')).map((system) => (
                      <button
                        key={system}
                        onClick={() => {
                          if (selectedSystems.includes(system)) {
                            setSelectedSystems(prev => prev.filter(s => s !== system));
                          } else if (selectedSystems.length < 2) {
                            setSelectedSystems(prev => [...prev, system]);
                          }
                        }}
                        className={`w-full p-3 border ${
                          selectedSystems.includes(system)
                            ? 'border-fuchsia-400 text-fuchsia-400 bg-fuchsia-900/20'
                            : 'border-cyan-400 text-cyan-400 hover:bg-cyan-400/10'
                        } rounded-lg transition-all duration-300`}
                      >
                        <div className="text-left">
                          <div>{SYSTEM_DESCRIPTIONS[system]?.name || system}</div>
                          {selectedSystems.includes(system) && (
                            <div className="text-xs mt-1 opacity-70">
                              {SYSTEM_DESCRIPTIONS[system]?.effects.map((effect, i) => (
                                <div key={i}>• {effect}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-fuchsia-400 mb-2">CYBERPUNK_SYSTEMS</h3>
                  <div className="space-y-2">
                    {SYSTEM_TYPES.filter(sys => sys.startsWith('CYBERPUNK')).map((system) => (
                      <button
                        key={system}
                        onClick={() => {
                          if (selectedSystems.includes(system)) {
                            setSelectedSystems(prev => prev.filter(s => s !== system));
                          } else if (selectedSystems.length < 2) {
                            setSelectedSystems(prev => [...prev, system]);
                          }
                        }}
                        className={`w-full p-3 border ${
                          selectedSystems.includes(system)
                            ? 'border-fuchsia-400 text-fuchsia-400 bg-fuchsia-900/20'
                            : 'border-cyan-400 text-cyan-400 hover:bg-cyan-400/10'
                        } rounded-lg transition-all duration-300`}
                      >
                        {system}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Analysis Display */}
              {selectedSystems.length === 2 && conflictAnalysis && (
                <div className="space-y-4">
                  {/* Critical Errors */}
                  <div className="border border-red-500 p-4 rounded-lg">
                    <div className="text-red-500 mb-2 flex items-center">
                      <span className="mr-2">⚠</span>
                      CRITICAL_ERRORS: {criticalErrors.length}
                    </div>
                    <div className="space-y-2">
                      {criticalErrors.map((error, index) => (
                        <div key={index} className="text-red-400 text-sm">
                          <div className="flex justify-between">
                            <span>{error.pattern}</span>
                            <span>SEV: {error.severity}</span>
                          </div>
                          <div className="text-gray-400 text-xs mt-1">{error.details}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Warnings */}
                  <div className="border border-yellow-500 p-4 rounded-lg">
                    <div className="text-yellow-500 mb-2 flex items-center">
                      <span className="mr-2">⚡</span>
                      WARNINGS: {warnings.length}
                    </div>
                    <div className="space-y-2">
                      {warnings.map((warning, index) => (
                        <div key={index} className="text-yellow-400 text-sm">
                          <div className="flex justify-between">
                            <span>{warning.pattern}</span>
                            <span>SEV: {warning.severity}</span>
                          </div>
                          <div className="text-gray-400 text-xs mt-1">{warning.details}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Convergence Progress */}
                  <div className="border border-cyan-400 p-4 rounded-lg">
                    <div className="text-fuchsia-400 mb-2">HARMONIC_CONVERGENCE</div>
                    <div className="bg-purple-900 rounded-full h-2">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${harmonicConvergence}%` }}
                        className="bg-cyan-400 h-full rounded-full"
                        transition={{ duration: 1 }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                {selectedSystems.length === 2 && !conflictAnalysis && (
                  <button
                    onClick={analyzeConflict}
                    className="flex-1 bg-transparent text-cyan-400 border-2 border-cyan-400 p-3 rounded-lg hover:bg-cyan-400/10 transition-colors"
                  >
                    ANALYZE_MERGE_CONFLICTS
                  </button>
                )}
                
                {conflictAnalysis && (
                  <button
                    onClick={initiateResolution}
                    className="flex-1 bg-transparent text-fuchsia-400 border-2 border-fuchsia-400 p-3 rounded-lg hover:bg-fuchsia-400/10 transition-colors"
                  >
                    INITIATE_RESOLUTION_PHASE_{resolutionPhase + 1}
                  </button>
                )}

                <button
                  onClick={onClose}
                  className="flex-1 bg-transparent text-red-400 border-2 border-red-400 p-3 rounded-lg hover:bg-red-400/10 transition-colors"
                >
                  TERMINATE_ANALYSIS
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MagiTechConflictModal;
